import { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import {
	ufoEventOnboardingWizardProjectCreation,
	ufoEventOnboardingWizardProjectCreationIncludingProvisioning,
} from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { JIRA_AI_NATIVE_SIGNUP_SOURCE } from '@/components/user-context/helpers/cohorts';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { WizardCreateProjectPayload } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useCloudStatus } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-cloud-status';
import { useCloudStatusRedirection } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-cloud-status-redirection';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { getProductQueryParamTeamType } from '../../../models/user-segmentation';
import { ANALYTICS_PREFIX } from '../common/constants';
import { useWizardConfiguration } from '../controllers';
import {
	useIsBasicSampleTasksForScrumOrSoftwareKanban,
	useIsCodingAgentChoice,
	useIsJFindInviteStepExperiment,
	useIsSimplifiedLandingViewWithBasicTasks,
	useIsSampleTasksForLandingGateNonTech,
} from '../hooks/experiment-hooks';
import { onRedirectFailure, onRedirectSuccess } from '../utils/redirect-logging';
import { ProjectConfig } from '../utils/types';

import { JiraOverlayShim } from './jira-overlay-shim';
import { WelcomeModalPageSwitcher } from './page-switcher/page-switcher';
import { useJfind1663FetchInterceptor } from './variant/jfind-1663-page-invite/hooks/use-jfind-1663-fetch-interceptor';

export const WelcomeModal = ({
	onComplete,
}: {
	onComplete: (taskId?: string, onRedirect?: () => void) => void;
}) => {
	// HOOKS
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { createCloudStatus, cloudId } = useCloudStatus();
	const isCreateRequestInProgress = useRef(false);
	const { signupId, taskIdParam, codingAgents, signupSource, jtbd, templateChoice } =
		useSearchParameters();
	const stargateClient = useBxpWebStargateClient();
	const { teamType, projectDetails } = useWizardConfiguration();
	const {
		sampleTasksForLandingGateNonTechIsEnrolledInTest,
		sampleTasksForLandingGateNonTechFireExposure,
	} = useIsSampleTasksForLandingGateNonTech();
	const { codingAgentChoiceIsEnrolledInTest } = useIsCodingAgentChoice();
	const {
		simplifiedLandingViewWithBasicTasksIsEnrolledInTest,
		simplifiedLandingViewWithBasicTasksFireExposure,
	} = useIsSimplifiedLandingViewWithBasicTasks(projectDetails.template);
	const {
		basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest,
		basicSampleTasksForScrumOrSoftwareKanbanFireExposure,
	} = useIsBasicSampleTasksForScrumOrSoftwareKanban();
	const { deferRedirectView, setDeferRedirectView } = useCloudStatusRedirection();
	const isJFindInviteStepExperiment = useIsJFindInviteStepExperiment();
	const [hasFiredProjectCreate, setHasFiredProjectCreate] = useState(false);

	// STATE
	const [projectConfig, setProjectConfig] = useState<ProjectConfig | null>(null);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: `${ANALYTICS_PREFIX}Screen`,
			attributes: {
				teamType: teamType,
				template: projectDetails.template,
			},
		}).fire();
	}, [createAnalyticsEvent, projectDetails.template, teamType]);

	// Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
	useEffect(() => {
		if (!deferRedirectView) {
			if (setDeferRedirectView) {
				setDeferRedirectView('jira-onboarding-wizard');
			}
		}
	});

	// eslint-disable-next-line complexity
	const createProject = useCallback(async () => {
		if (projectConfig && cloudId) {
			const shouldForceSoftwareDevelopmentAgenticTemplate =
				checkFeatureFlagGate(featureFlags.spiceAiSdlcAgenticTemplateForNewSites) &&
				signupSource === JIRA_AI_NATIVE_SIGNUP_SOURCE;

			ufoEventOnboardingWizardProjectCreation.start();

			ufoEventOnboardingWizardProjectCreation.setCustomData({ signupId });

			ufoEventOnboardingWizardProjectCreationIncludingProvisioning.startSubstep(
				UfoCustomTimingKey.ONBOARDING_WIZARD_PROJECT_CREATE_API_CALL,
			);

			const sampleDataVersion =
				codingAgentChoiceIsEnrolledInTest ||
				sampleTasksForLandingGateNonTechIsEnrolledInTest ||
				shouldForceSoftwareDevelopmentAgenticTemplate
					? 'SPACE_ONBOARDING'
					: simplifiedLandingViewWithBasicTasksIsEnrolledInTest
						? 'GENERIC'
						: basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest
							? 'GENERICV2'
							: undefined;

			// Parse the coding agents selected on the coding agent choice
			// screen (persisted as a comma-separated `coding_agents` query
			// param, where "NONE" indicates an explicit empty selection).
			const parsedAgentTools =
				codingAgentChoiceIsEnrolledInTest && codingAgents && codingAgents !== 'NONE'
					? codingAgents
							.split(',')
							.map((agent) => agent.trim())
							.filter(Boolean)
					: [];
			const selectedAgentTools = parsedAgentTools.length > 0 ? parsedAgentTools : undefined;

			try {
				if (simplifiedLandingViewWithBasicTasksFireExposure) {
					simplifiedLandingViewWithBasicTasksFireExposure();
				}

				if (basicSampleTasksForScrumOrSoftwareKanbanFireExposure) {
					basicSampleTasksForScrumOrSoftwareKanbanFireExposure();
				}

				if (sampleTasksForLandingGateNonTechFireExposure) {
					sampleTasksForLandingGateNonTechFireExposure();
				}

				isCreateRequestInProgress.current = true;

				const projectPayload: WizardCreateProjectPayload = {
					projectName: projectConfig.projectName,
					issueTypes: projectConfig.issueTypes,
					statusNames: projectConfig.columns,
					projectKey: projectDetails.projectKey,
					template: projectDetails.template,
					cloudId: cloudId,
					teamType: getProductQueryParamTeamType(teamType),
					...(selectedAgentTools ? { selectedAgentTools } : {}),
					customDetail: {
						enableExtraFields: true,
						...(sampleDataVersion ? { sampleDataVersion } : {}),
					},
				};
				const { taskId } = await stargateClient.wizardProjectCreate(projectPayload);

				if (isJFindInviteStepExperiment) {
					// set the taskId if not already present, read by projectCreateTaskPolling to determine projectId
					const url = new URL(window.location.href);
					if (!url.searchParams.get('taskId')) {
						url.searchParams.set('taskId', taskId);
						history.replaceState(null, '', url.toString());
					}
					setHasFiredProjectCreate(true);
				}

				ufoEventOnboardingWizardProjectCreation.success({
					customData: {
						template: projectDetails.template,
						teamType: getProductQueryParamTeamType(teamType),
						sampleDataVersion,
					},
				});

				createAnalyticsEvent({
					action: 'created',
					actionSubject: `${ANALYTICS_PREFIX}Project`,
					eventType: 'track',
					attributes: {
						template: projectDetails.template,
						teamType,
						sampleDataVersion,
						jtbd,
						templateChoice,
					},
				}).fire();

				if (!isJFindInviteStepExperiment) {
					onComplete(taskId, onRedirectSuccess(projectDetails.template));
				}
			} catch (error) {
				ufoEventOnboardingWizardProjectCreation.failure({
					customData: {
						template: projectDetails.template,
						teamType: getProductQueryParamTeamType(teamType),
						sampleDataVersion,
					},
					error,
				});

				createAnalyticsEvent({
					action: 'failed',
					actionSubject: `${ANALYTICS_PREFIX}Project`,
					eventType: 'track',
					attributes: {
						template: projectDetails.template,
						teamType,
						sampleDataVersion,
						jtbd,
						templateChoice,
					},
				}).fire();

				if (isJFindInviteStepExperiment) {
					// set the taskId if not already present to increment current page, Invite Step will show partial experience
					const url = new URL(window.location.href);
					if (!url.searchParams.get('taskId')) {
						url.searchParams.set('taskId', '');
						history.replaceState(null, '', url.toString());
					}
					setHasFiredProjectCreate(true);
				}

				if (!isJFindInviteStepExperiment) {
					onComplete(undefined, onRedirectFailure(projectDetails.template, error));
				} else {
					onRedirectFailure(projectDetails.template, error);
				}
			}
		}
	}, [
		createAnalyticsEvent,
		projectConfig,
		cloudId,
		signupId,
		sampleTasksForLandingGateNonTechIsEnrolledInTest,
		sampleTasksForLandingGateNonTechFireExposure,
		codingAgentChoiceIsEnrolledInTest,
		codingAgents,
		signupSource,
		simplifiedLandingViewWithBasicTasksFireExposure,
		basicSampleTasksForScrumOrSoftwareKanbanFireExposure,
		simplifiedLandingViewWithBasicTasksIsEnrolledInTest,
		basicSampleTasksForScrumOrSoftwareKanbanIsEnrolledInTest,
		projectDetails.projectKey,
		projectDetails.template,
		teamType,
		jtbd,
		templateChoice,
		stargateClient,
		isJFindInviteStepExperiment,
		onComplete,
	]);

	useEffect(() => {
		// Only trigger creating project once the site is ready
		let canCreateProject;

		if (isJFindInviteStepExperiment) {
			canCreateProject =
				projectConfig && createCloudStatus === 'success' && cloudId && !taskIdParam;
		} else {
			canCreateProject = projectConfig && createCloudStatus === 'success' && cloudId;
		}

		if (canCreateProject && !isCreateRequestInProgress.current) {
			createProject();
		}
	}, [
		createCloudStatus,
		cloudId,
		createProject,
		projectConfig,
		taskIdParam,
		isJFindInviteStepExperiment,
	]);

	useEffect(() => {
		if (createCloudStatus === 'success') {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'succeeded',
				actionSubject: 'provisioning',
				actionSubjectId: 'jiraAnimatedProvisioningScreen',
				source: '/ondemand/signup/confirmation',
				attributes: {
					atlasId: 'ATLAS-56326',
					reactComponent: 'WelcomeModal',
					template: projectDetails.template,
					teamType: teamType,
				},
			}).fire();
		}
	}, [createAnalyticsEvent, createCloudStatus, projectDetails.template, teamType]);

	useJfind1663FetchInterceptor({ cloudId });

	return (
		<FeatureExposed experimentKey="jfind-1565-sign-up-flow-overlay" group="Test">
			<JiraOverlayShim>
				<WelcomeModalPageSwitcher
					setProjectConfig={setProjectConfig}
					onComplete={onComplete}
					hasFiredProjectCreate={hasFiredProjectCreate}
				/>
			</JiraOverlayShim>
		</FeatureExposed>
	);
};
